<script setup lang="ts">
/**
 * Full-page state for "the session check got no answer" (offline, timeout,
 * 5xx). It is not a sign-out: the route is kept, and Retry (or the browser
 * coming back online) checks the session again.
 */

import { onBeforeUnmount, onMounted, ref } from 'vue'
import { Button } from 'frappe-ui'
import { session } from '../session'
import { retryBootAndRoute } from '../router'

const retrying = ref(false)

async function retry() {
  if (retrying.value) return
  retrying.value = true
  try {
    await retryBootAndRoute()
  } finally {
    retrying.value = false
  }
}

onMounted(() => window.addEventListener('online', retry))
onBeforeUnmount(() => window.removeEventListener('online', retry))
</script>

<template>
  <main
    class="flex min-h-screen items-center justify-center bg-surface-base px-6 py-12"
  >
    <div
      class="flex max-w-md flex-col items-center text-center"
      role="alert"
      aria-live="assertive"
    >
      <span
        class="lucide-cloud-off size-10 text-ink-gray-4"
        aria-hidden="true"
      />
      <h1 class="mt-4 text-xl font-semibold text-ink-gray-9">
        Can't reach the server
      </h1>
      <p class="mt-2 text-sm text-ink-gray-6">
        Check your internet connection. You have not been signed out, and you'll
        return to this page once the server answers.
      </p>
      <p
        v-if="session.unreachable"
        class="mt-2 break-words text-xs text-ink-gray-5"
      >
        {{ session.unreachable }}
      </p>
      <Button
        class="mt-6"
        variant="solid"
        theme="blue"
        label="Retry"
        icon-left="lucide-rotate-cw"
        :loading="retrying"
        @click="retry"
      />
    </div>
  </main>
</template>
